import type { CustomerModuleNavigationKey, UserRole } from '~/prisma/enums'

interface NavigationConfigItem {
  icon: string
  link: string
  allowedRoles?: UserRole[]
}

const navigationConfigs: Readonly<Record<CustomerModuleNavigationKey, NavigationConfigItem>> = {
  profile: {
    icon: 'material-symbols:person-edit-outline-rounded',
    link: '/my-hos/profile',
  },
  shopRent: {
    icon: 'material-symbols:shopping-cart-outline-rounded',
    link: '/shop/rent',
  },
  shopBuy: {
    icon: 'material-symbols:shopping-cart-outline-rounded',
    link: '/shop/buy',
  },
  foreignMachinery: {
    icon: 'material-symbols:location-away-outline-rounded',
    link: '/my-hos/foreign-machinery',
  },
  rentals: {
    icon: 'material-symbols:calendar-clock-outline',
    link: '/my-hos/rental',
  },
  sales: {
    icon: 'material-symbols:shopping-cart-outline',
    link: '/my-hos/sale',
  },
  serviceProject: {
    icon: 'material-symbols:design-services-outline-rounded',
    link: '/my-hos/service-project',
  },
  special: {
    icon: 'material-symbols:award-star-outline-rounded',
    link: '/my-hos/special',
  },
  machinery: {
    icon: 'material-symbols:forklift-outline-rounded',
    link: '/my-hos/machinery',
  },
  quickSale: {
    icon: 'material-symbols:description-outline-rounded',
    link: '/my-hos/quick-sale',
  },
  serviceProjectReporting: {
    icon: 'material-symbols:design-services-outline-rounded',
    link: '/my-hos/service-project/reporting',
  },
  toMainApp: {
    icon: 'material-symbols:open-in-new-rounded',
    link: '/',
    allowedRoles: ['extStorageUser'],
  },
}

const defaultNavigationConfigPerRole: Readonly<Partial<Record<UserRole, CustomerModuleNavigationKey[]>>> = {
  partnerSales: ['machinery', 'quickSale'],
  externalServiceProjectStakeholder: ['serviceProjectReporting'],
  extStorageUser: ['toMainApp'],
}

type NavigationConfigItemWithKey = [CustomerModuleNavigationKey, NavigationConfigItem]
export default () => {
  const { canAccessPage, hasAnyAssignedRole, roles } = useAuthorization()
  const { customerQuery } = useCustomerModuleQuery()
  const { data, isLoading, suspense } = customerQuery.getCustomizedNavigationConfigs(hasAnyAssignedRole(['customerUser'], false))

  const allowedUserNavigationConfigs = computed((): NavigationConfigItemWithKey[] => {
    return Object.entries(navigationConfigs).filter(([_, config]) => {
      if (config.allowedRoles && config.allowedRoles.length > 0) {
        return hasAnyAssignedRole(config.allowedRoles, false).value
      }
      return canAccessPage(config.link)
    }).map(([key, value]) => {
      // Set correct key type as it was casted to string from Object.entries
      return [key as CustomerModuleNavigationKey, value]
    })
  })

  const currentUserNavigationConfigs = computed((): NavigationConfigItemWithKey[] => {
    if (isLoading.value) {
      return []
    }

    if (data.value) {
      const allowedConfigs = Object.fromEntries(allowedUserNavigationConfigs.value)
      const configsToUse = new Set<CustomerModuleNavigationKey>(data.value.customizedNavigationConfig)

      // Add default configs based on user roles
      for (const role of roles.value) {
        const defaultConfigs = defaultNavigationConfigPerRole[role]
        defaultConfigs?.forEach(config => configsToUse.add(config))
      }

      // Filter out configs that are not allowed
      const customizedConfigs: NavigationConfigItemWithKey[] = []
      for (const config of configsToUse) {
        if (allowedConfigs[config]) {
          customizedConfigs.push([config, allowedConfigs[config]])
        }
      }
      return customizedConfigs
    }
    return allowedUserNavigationConfigs.value
  })

  return {
    allowedUserNavigationConfigs,
    currentUserNavigationConfigs,
    isLoading,
    suspense,
  }
}
